import React, { useEffect, useState } from 'react';

import { Icon, Text, cn } from '@eluve/components';
import { DynamicArtifactTemplate } from '@eluve/llm-outputs';

/**
 * Schema-aware "fake streaming" progress for note generation. The real
 * call is a single 10–15s request, so we synthesize a plausible work
 * log from the template being generated: section-specific lines built
 * from the block labels, interleaved with generic clinical-processing
 * lines. One line surfaces every 2.5s and types itself out.
 */

const OPENING_MESSAGES = [
  'Reading the transcript…',
  'Identifying who said what…',
  'Gathering medically relevant facts…',
];

const GENERIC_MESSAGES = [
  'Cross-checking symptoms against the history…',
  'Extracting medication names and dosages…',
  'Noting pertinent negatives…',
  'Flagging follow-up items mentioned in the visit…',
  'Separating patient-reported details from clinical findings…',
  'Checking for allergies and contraindications…',
  'Listening for timing and duration cues…',
  'Matching complaints to the relevant sections…',
  'Reconciling details mentioned more than once…',
  'Filtering out small talk…',
];

const CLOSING_MESSAGES = [
  'Applying the template structure…',
  'Tidying formatting and wording…',
  'Running a final consistency check…',
  'Almost there…',
];

const MAX_MESSAGES = 30;
const MESSAGE_INTERVAL_MS = 2500;
const TYPE_INTERVAL_MS = 18;
const MAX_PLACEHOLDERS_PER_SECTION = 2;

/**
 * Extracts [bracket placeholder] tokens from section content, e.g.
 * "Patient is a [age]-year-old [sex] with [relevant history]." →
 * ['age', 'sex', 'relevant history']. Skips markdown links and tokens
 * that don't read like prose.
 */
export const extractBracketPlaceholders = (text: string): string[] => {
  // Section bodies arrive as markdown, where brackets are serialized as
  // \[ and \]; unescape so the tokens match (and no backslash leaks
  // into the captured value)
  const unescaped = text.replace(/\\(.)/g, '$1');
  const seen = new Set<string>();
  const placeholders: string[] = [];
  for (const match of unescaped.matchAll(/\[([^[\]\n]+?)\](?!\()/g)) {
    const value = match[1]!.trim().toLowerCase();
    const isProse =
      value.length >= 3 &&
      value.length <= 60 &&
      value.split(/\s+/).length <= 6 &&
      /[a-z]/.test(value);
    if (isProse && !seen.has(value)) {
      seen.add(value);
      placeholders.push(value);
    }
  }
  return placeholders;
};

const PLACEHOLDER_PHRASES = [
  (p: string) => `Listening for the ${p}…`,
  (p: string) => `Filling in the ${p}…`,
  (p: string) => `Scanning the visit for the ${p}…`,
];

/**
 * Builds the candidate status lines for a template: opening lines, then
 * per-section lines in template order — flavored with the section's own
 * [bracket placeholders] — with generic processing lines interleaved,
 * then closing lines. Capped at 30.
 */
export const buildGenerationStatusMessages = (
  template: DynamicArtifactTemplate,
): string[] => {
  const sectionLines: string[] = [];
  let phraseIndex = 0;

  const pushTextBlockLines = (block: {
    label: string;
    description?: string;
    placeholderText?: string;
  }) => {
    sectionLines.push(`Building "${block.label}" section…`);
    const placeholders = extractBracketPlaceholders(
      block.description ?? block.placeholderText ?? '',
    );
    if (placeholders.length === 0) {
      sectionLines.push(
        `Pulling visit details relevant to ${block.label.toLowerCase()}…`,
      );
      return;
    }
    placeholders.slice(0, MAX_PLACEHOLDERS_PER_SECTION).forEach((p) => {
      const phrase =
        PLACEHOLDER_PHRASES[phraseIndex % PLACEHOLDER_PHRASES.length]!;
      sectionLines.push(phrase(p));
      phraseIndex += 1;
    });
  };

  template.blocks.forEach((block) => {
    if (block.type === 'group') {
      sectionLines.push(`Organizing the ${block.label} sections…`);
      block.blocks.forEach((child) => {
        if (child.type === 'text') {
          pushTextBlockLines(child);
        }
      });
    } else if (block.type === 'text') {
      pushTextBlockLines(block);
    }
  });

  // Interleave a generic line after every couple of section lines so the
  // log reads like varied work rather than a checklist sweep
  const generics = [...GENERIC_MESSAGES].sort(() => Math.random() - 0.5);
  const middle: string[] = [];
  let genericIndex = 0;
  sectionLines.forEach((line, index) => {
    middle.push(line);
    if (index % 2 === 1 && genericIndex < generics.length) {
      middle.push(generics[genericIndex]!);
      genericIndex += 1;
    }
  });

  const budgetForMiddle =
    MAX_MESSAGES - OPENING_MESSAGES.length - CLOSING_MESSAGES.length;

  return [
    ...OPENING_MESSAGES,
    ...middle.slice(0, budgetForMiddle),
    ...CLOSING_MESSAGES,
  ];
};

export interface GenerationProgressLogProps {
  messages: string[];
}

export const GenerationProgressLog: React.FC<GenerationProgressLogProps> = ({
  messages,
}) => {
  const [lineIndex, setLineIndex] = useState(0);
  const [typedLength, setTypedLength] = useState(0);

  const currentMessage =
    messages[Math.min(lineIndex, messages.length - 1)] ?? '';

  // Surface the next line on a fixed cadence; the last line just keeps
  // its caret blinking until the real response lands
  useEffect(() => {
    const id = setInterval(() => {
      setLineIndex((index) => Math.min(index + 1, messages.length - 1));
    }, MESSAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [messages.length]);

  // Typewriter for the current line
  useEffect(() => {
    setTypedLength(0);
    const id = setInterval(() => {
      setTypedLength((length) => {
        if (length >= currentMessage.length) {
          clearInterval(id);
          return length;
        }
        return length + 1;
      });
    }, TYPE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [lineIndex, currentMessage]);

  return (
    <div className="flex flex-1 flex-col gap-2.5 pt-2">
      {messages.slice(0, lineIndex + 1).map((message, index) => {
        const isCurrent = index === lineIndex;
        return (
          <div key={index} className="flex items-center gap-2">
            {isCurrent ? (
              <Icon
                name="WandSparkles"
                size="sm"
                className="shrink-0 text-text-indigo"
              />
            ) : (
              <Icon
                name="Check"
                size="sm"
                className="shrink-0 text-text-tertiary"
              />
            )}
            <Text
              size="sm"
              className={cn(
                isCurrent ? 'text-text-primary' : 'text-text-tertiary',
              )}
            >
              {isCurrent ? message.slice(0, typedLength) : message}
              {isCurrent && (
                <span className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse bg-text-indigo align-middle" />
              )}
            </Text>
          </div>
        );
      })}
    </div>
  );
};
